import React, { useState, useRef, useEffect } from 'react';

interface TpinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (tpin: string) => void | Promise<void>;
  isLoading?: boolean;
  error?: string;
  maskedMobile?: string;
}

const TpinModal: React.FC<TpinModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  error = '',
  maskedMobile = ''
}) => {
  const [tpin, setTpin] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTpin('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTpin(val);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tpin.length === 6 && !isLoading) {
      onSubmit(tpin);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10001] p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔐</span>
            <h2 className="text-lg font-bold text-slate-800 m-0">Verify TPIN</h2>
          </div>
          <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-100 cursor-pointer text-2xl" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5">
          <p className="text-slate-600 text-sm mb-4">
            A 6-digit TPIN has been sent to your registered mobile number {maskedMobile ? `(****${maskedMobile})` : ''}. Enter it below to authorize this payout. Your TPIN is valid for 15 days and can be reused for multiple payouts.
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mb-5">
            <label htmlFor="tpin" className="block text-sm font-semibold text-slate-700 mb-2">6-Digit TPIN</label>
            <input
              ref={inputRef}
              id="tpin"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={tpin}
              onChange={handleChange}
              placeholder="000000"
              disabled={isLoading}
              className="w-full py-3 px-4 text-center text-2xl font-mono tracking-[0.5em] border-2 border-slate-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none disabled:opacity-60"
              autoComplete="one-time-code"
            />
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-semibold text-sm cursor-pointer hover:bg-slate-50 flex items-center justify-center gap-2">
              Cancel
            </button>
            <button
              type="submit"
              disabled={tpin.length !== 6 || isLoading}
              className="flex-1 py-3 px-4 rounded-xl bg-violet-600 text-white font-semibold text-sm cursor-pointer hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                <>✓ Verify & Submit Payout</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TpinModal;
